/**
 * Pure Modash PDF parsing utilities.
 *
 * NO PDF.js dependency here — every function is testable with plain strings.
 * parsePdf.ts imports from here and adds the PDF.js extraction layer.
 */

// ── Public output types ────────────────────────────────────────────────────

/** Unified row shape for all distribution tables (age, geo, interests, brands). */
export interface AudienceMetricRow {
  label:            string;
  followers_pct:    number | null;  // decimal 0..1
  followers_count:  number | null;
  likers_pct:       number | null;
  likers_count:     number | null;
}

export interface ParsedModashReport {
  // Creator identity (extracted from header when possible)
  platform:      'instagram' | 'tiktok' | 'youtube' | 'other' | null;
  handle:        string | null;
  profile_url:   string | null;
  location_text: string | null;

  // Headline metrics
  followers:       number | null;
  engagement_rate: number | null;  // decimal, e.g. 0.0008
  avg_likes:       number | null;
  avg_views:       number | null;

  // ── Creator's OWN signals (highest weight for product matching) ───────
  // These are the creator's declared interests and brand partnerships —
  // NOT the audience's. They reflect the type of content the creator makes.
  creator_interests:     string[] | null;  // e.g. ["Beauty & Cosmetics", "Luxury Goods"]
  creator_brand_affinity: string[] | null; // e.g. ["Chanel", "Kylie Cosmetics"]

  // ── Audience distributions ────────────────────────────────────────────
  gender_distribution:     { male?: number; female?: number } | null;
  age_distribution_overall: AudienceMetricRow[] | null;
  age_distribution_male:    AudienceMetricRow[] | null;
  age_distribution_female:  AudienceMetricRow[] | null;
  geo_distribution:         AudienceMetricRow[] | null;
  interests_ranked:         AudienceMetricRow[] | null;  // audience interests (with %)
  brand_affinity_ranked:    AudienceMetricRow[] | null;  // audience brand affinities

  // Extra 2-col tables
  popular_hashtags: Array<{ tag: string; pct: number }> | null;
  popular_mentions: Array<{ mention: string; pct: number }> | null;

  // Inferred content categories (derived from creator interests first, then text scan)
  primary_category:     string | null;
  secondary_categories: string[];

  // Diagnostics
  parseWarnings: string[];
  debug: {
    extractedSections: Record<string, string>;
    detectedTables: Array<{ title: string; kind: 'two_col' | 'three_col'; rowCount: number }>;
  };
}

// Internal 3-col row before mapping
export interface ThreeColRow {
  label:     string;
  likers:    { count: number | null; pct: number | null } | null;
  followers: { count: number | null; pct: number | null } | null;
}

export interface TwoColRow { label: string; pctDecimal: number }
export interface ParsedTwoColTable { title: string; rows: TwoColRow[] }

// ── Normalisation helpers (all exported for tests) ─────────────────────────

export function normalizeLine(s: string): string {
  return s
    .replace(/\u00a0/g, ' ')   // nbsp
    .replace(/\u2013|\u2014/g, '-')
    .replace(/[ \t]+/g, ' ')
    .trim();
}

/** "574.7k" → 574700 | "1.2M" → 1200000 | "37,577" → 37577 */
export function parseCompactNumber(s: string): number | null {
  const clean = s.replace(/,/g, '').trim();
  const m = clean.match(/^([\d.]+)\s*([KkMmBb]?)$/);
  if (!m) return null;
  const n = parseFloat(m[1]);
  if (isNaN(n)) return null;
  const mult = ({ k: 1_000, m: 1_000_000, b: 1_000_000_000 } as Record<string, number>)[m[2].toLowerCase()] ?? 1;
  return Math.round(n * mult);
}

/** "516,901" → 516901 */
export function parseIntWithCommas(s: string): number | null {
  const n = parseInt(s.replace(/,/g, ''), 10);
  return isNaN(n) ? null : n;
}

/**
 * Convert a percent string to 0..1 decimal.
 *   "8.39%"  → 0.0839
 *   "8.39"   → 0.0839  (treat as 0-100 scale if > 1)
 *   "89.94"  → 0.8994
 *   "0.032"  → 0.032   (already decimal ≤ 1)
 *   "0.08%"  → 0.0008
 */
export function parsePercentToDecimal(s: string): number | null {
  const trimmed = s.trim();
  const hasSymbol = trimmed.endsWith('%');
  const numStr = hasSymbol ? trimmed.slice(0, -1).trim() : trimmed;
  const num = parseFloat(numStr);
  if (isNaN(num) || num < 0) return null;
  if (hasSymbol) return parseFloat((num / 100).toFixed(6));
  if (num > 1 && num <= 100) return parseFloat((num / 100).toFixed(6));
  return parseFloat(num.toFixed(6));
}

export function safeNumber(x: unknown): number | null {
  const n = typeof x === 'string' ? parseFloat(x) : Number(x);
  return isNaN(n) ? null : n;
}

// ── Section headings registry ──────────────────────────────────────────────

export const ALL_HEADINGS = [
  // ── Creator-level sections (creator's OWN interests / brand partnerships) ──
  'Creator Interests',
  'Creator brand affinity',
  // ── Audience table sections ────────────────────────────────────────────
  'Gender split',
  'Age split',
  'Male age split',
  'Female age split',
  'Location by Country',
  'Location by City',
  'Audience interests',
  'Follower Interests',
  'Audience brand affinity',
  'Follower brand affinity',
  'Popular hashtags',
  'Popular mentions',
  'Most popular content',
  'Sponsored posts',
] as const;

function escapeRegex(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/**
 * Extract text between `heading` and the next known heading.
 * Matches headings that appear on their own line (with optional leading whitespace).
 */
export function extractSectionByHeadings(
  fullText: string,
  heading: string,
  headings: readonly string[] = ALL_HEADINGS,
): string | null {
  // Match heading on its own line
  const headRe = new RegExp(
    '(?:^|\\n)[ \\t]*' + escapeRegex(heading) + '[ \\t]*(?:\\n|$)',
    'i',
  );
  const headMatch = headRe.exec(fullText);
  if (!headMatch) return null;

  const contentStart = headMatch.index + headMatch[0].length;
  let end = fullText.length;

  for (const other of headings) {
    if (other.toLowerCase() === heading.toLowerCase()) continue;
    const otherRe = new RegExp(
      '(?:^|\\n)[ \\t]*' + escapeRegex(other) + '[ \\t]*(?:\\n|$)',
      'i',
    );
    const m = otherRe.exec(fullText.slice(contentStart));
    if (m) {
      const absIdx = contentStart + m.index;
      if (absIdx < end) end = absIdx;
    }
  }

  const result = fullText.slice(contentStart, end).trim();
  return result.length > 0 ? result : null;
}

// ── 2-column table parser ──────────────────────────────────────────────────

/**
 * Parse rows of the form  "<LABEL>  <PCT>%"
 * The label may contain spaces, commas, @, # etc.
 * Returns rows sorted by pct descending.
 */
export function parseTwoColPercentRows(sectionText: string): TwoColRow[] {
  const rows: TwoColRow[] = [];
  for (const raw of sectionText.split('\n')) {
    const line = normalizeLine(raw);
    // Must end with a number followed by %
    const m = line.match(/^(.+?)\s+([\d.]+)%\s*$/);
    if (!m) continue;
    const label = m[1].trim();
    const pctDecimal = parsePercentToDecimal(m[2] + '%');
    if (!label || pctDecimal == null) continue;
    // Skip lines that look like headers (containing "Likers"/"Followers" keywords)
    if (/likers|followers/i.test(label)) continue;
    rows.push({ label, pctDecimal });
  }
  return rows;
}

// ── 3-column (Likers / Followers) tolerant parser ─────────────────────────

const CELL_RE = /(\d[\d,]*)\s*\/\s*([\d.]+)%\s*$/;
const MISSING_RE = /\s+-\s*$/;

function parseCellFromEnd(s: string): {
  count: number | null;
  pct: number | null;
  remaining: string;
  present: boolean;
} {
  // Missing cell: ends with " -"
  if (MISSING_RE.test(s)) {
    return {
      count: null, pct: null,
      remaining: s.replace(MISSING_RE, '').trim(),
      present: false,
    };
  }
  const m = CELL_RE.exec(s);
  if (m) {
    return {
      count: parseIntWithCommas(m[1]),
      pct:   parsePercentToDecimal(m[2] + '%'),
      remaining: s.slice(0, m.index).trim(),
      present: true,
    };
  }
  // No cell found — return whole string as remaining
  return { count: null, pct: null, remaining: s.trim(), present: false };
}

/**
 * Parse rows of the 3-column Modash table:
 *   "<LABEL> <likersCount> / <likersPct>%  <followersCount> / <followersPct>%"
 * Tolerates missing cells ("-") without stopping.
 */
export function parseThreeColRows(sectionText: string): ThreeColRow[] {
  const rows: ThreeColRow[] = [];
  for (const raw of sectionText.split('\n')) {
    const line = normalizeLine(raw);
    if (!line) continue;
    // Skip header lines
    if (/^\s*(likers|followers)\s*$/i.test(line)) continue;
    if (/likers\s+followers/i.test(line)) continue;

    // Parse followers (rightmost cell)
    const follCell = parseCellFromEnd(line);
    // Parse likers from remaining
    const likeCell = parseCellFromEnd(follCell.remaining);
    const label = likeCell.remaining.trim();

    if (!label || label === '-') continue;
    // Skip if this looks like a section heading (no numeric data found at all and text is short title)
    if (!follCell.present && !likeCell.present) {
      // Check if it's a data row: it should have had at least one pct somewhere
      if (!/[\d]/.test(line)) continue;
    }

    rows.push({
      label,
      likers:    likeCell.present ? { count: likeCell.count, pct: likeCell.pct } : null,
      followers: follCell.present ? { count: follCell.count, pct: follCell.pct } : null,
    });
  }
  return rows;
}

// ── Table kind detection ───────────────────────────────────────────────────

export function detectTableKind(sectionText: string): 'two_col' | 'three_col' {
  const lines = sectionText.split('\n').map(normalizeLine).filter(Boolean);
  let threeCol = 0;
  let twoCol   = 0;
  for (const line of lines.slice(0, 15)) {
    if (/\d[\d,]*\s*\/\s*[\d.]+%/.test(line)) threeCol++;
    else if (/^.+\s[\d.]+%\s*$/.test(line)) twoCol++;
  }
  return threeCol > 0 ? 'three_col' : 'two_col';
}

// ── Multi 2-col table block parser ────────────────────────────────────────

/**
 * Parse a block that contains MULTIPLE 2-col tables separated by title lines.
 * Title line: not ending in %, contains letters, ≤ 80 chars, not a data value.
 */
export function parseMultiTwoColTables(blockText: string): ParsedTwoColTable[] {
  const tables: ParsedTwoColTable[] = [];
  let current: ParsedTwoColTable | null = null;

  for (const raw of blockText.split('\n')) {
    const line = normalizeLine(raw);
    if (!line) continue;

    const dataMatch = line.match(/^(.+?)\s+([\d.]+)%\s*$/);
    if (dataMatch) {
      const pctDecimal = parsePercentToDecimal(dataMatch[2] + '%');
      if (pctDecimal == null) continue;
      if (!current) {
        current = { title: '', rows: [] };
        tables.push(current);
      }
      current.rows.push({ label: dataMatch[1].trim(), pctDecimal });
    } else if (line.length <= 80 && /[a-zA-Z]/.test(line) && !line.match(/^\d+$/)) {
      // Treat as section title
      current = { title: line, rows: [] };
      tables.push(current);
    }
  }

  return tables.filter(t => t.rows.length > 0);
}

// ── Converters ─────────────────────────────────────────────────────────────

export function threeColToAudienceRows(rows: ThreeColRow[]): AudienceMetricRow[] {
  return rows.map(r => ({
    label:           r.label,
    followers_pct:   r.followers?.pct   ?? null,
    followers_count: r.followers?.count ?? null,
    likers_pct:      r.likers?.pct      ?? null,
    likers_count:    r.likers?.count    ?? null,
  }));
}

export function twoColToAudienceRows(rows: TwoColRow[]): AudienceMetricRow[] {
  return rows.map(r => ({
    label:           r.label,
    followers_pct:   r.pctDecimal,
    followers_count: null,
    likers_pct:      null,
    likers_count:    null,
  }));
}

// ── Core metric extractors ─────────────────────────────────────────────────

export function extractFollowers(text: string): number | null {
  const patterns = [
    /followers?\s*\n\s*([\d,.]+\s*[KkMmBb]?)/i,
    /([\d,.]+\s*[KkMmBb]?)\s*\nfollowers?/i,
    /followers?\s*[:\-–]?\s*([\d,.]+\s*[KkMmBb]?)/i,
    /([\d,.]+\s*[KkMmBb]?)\s*followers?/i,
    /subscribers?\s*[:\-–]?\s*([\d,.]+\s*[KkMmBb]?)/i,
  ];
  for (const p of patterns) {
    const m = text.match(p);
    if (m?.[1]) {
      const n = parseCompactNumber(m[1].replace(/\s/g, ''));
      if (n && n > 100) return n;
    }
  }
  return null;
}

export function extractEngagementRate(text: string): number | null {
  const patterns = [
    /engagement\s+rate\s*\n\s*([\d.]+\s*%)/i,
    /engagement\s+rate\s*[:\-–]?\s*([\d.]+\s*%)/i,
    /([\d.]+\s*%)\s*engagement\s+rate/i,
  ];
  for (const p of patterns) {
    const m = text.match(p);
    if (m?.[1]) {
      const v = parsePercentToDecimal(m[1]);
      if (v != null && v >= 0 && v <= 1) return v;
    }
  }
  return null;
}

export function extractAvgLikes(text: string): number | null {
  const patterns = [
    /avg\.?\s*likes?\s*\n\s*([\d,.]+\s*[KkMmBb]?)/i,
    /avg\.?\s*likes?\s*[:\-–]?\s*([\d,.]+\s*[KkMmBb]?)/i,
    /average\s+likes?\s*[:\-–]?\s*([\d,.]+\s*[KkMmBb]?)/i,
  ];
  for (const p of patterns) {
    const m = text.match(p);
    if (m?.[1]) {
      const n = parseCompactNumber(m[1].replace(/\s/g, ''));
      if (n != null) return n;
    }
  }
  return null;
}

export function extractAvgViews(text: string): number | null {
  const patterns = [
    /avg\.?\s*(?:views?|plays?|reels?\s*plays?)\s*\n\s*([\d,.]+\s*[KkMmBb]?)/i,
    /avg\.?\s*(?:views?|plays?|reels?\s*plays?)\s*[:\-–]?\s*([\d,.]+\s*[KkMmBb]?)/i,
  ];
  for (const p of patterns) {
    const m = text.match(p);
    if (m?.[1]) {
      const n = parseCompactNumber(m[1].replace(/\s/g, ''));
      if (n != null) return n;
    }
  }
  return null;
}

// ── Creator-section parsers ────────────────────────────────────────────────

/**
 * Parse a plain label list (no percentages or counts) as used by Modash for
 * "Creator Interests" and "Creator brand affinity" sections.
 *
 * Example section text:
 *   "Beauty & Cosmetics\nJewellery & Watches\nLuxury Goods"
 *   "Chanel\nKylie Cosmetics\nMiu Miu"
 *
 * Returns an ordered list of label strings (most prominent first = top of list).
 */
export function parseCreatorLabelList(sectionText: string | null): string[] | null {
  if (!sectionText) return null;
  const labels: string[] = [];
  for (const raw of sectionText.split('\n')) {
    const line = normalizeLine(raw);
    if (!line) continue;
    // Skip header/section-title lines
    if (/^(likers|followers|interests|brand\s*affinity|creator\s*interests?|creator\s*brand)$/i.test(line)) continue;
    // Skip standalone numbers / percentages
    if (/^[\d.,]+%?$/.test(line)) continue;
    // Skip very long lines (likely chart data or PDF artefacts)
    if (line.length > 120) continue;
    // Skip lines that look like engagement-rate / metric labels
    if (/engagement rate|avg\.|average|estimated|impressions|reels?|stories?/i.test(line)) continue;
    labels.push(line);
  }
  return labels.length > 0 ? labels : null;
}

// Client-side mapping: creator interest label → primary category keyword.
// Used to derive primary_category from the creator's OWN interest list
// (more reliable than full-text scanning).
// Patterns are intentionally broad substrings so compound Modash labels
// like "Clothes, Shoes, Handbags & Accessories" match naturally.
const CREATOR_INTEREST_TO_CATEGORY: Array<{ patterns: string[]; category: string }> = [
  { patterns: ['beauty', 'cosmetics', 'makeup', 'skincare', 'haircare', 'nail', 'lash', 'grooming'],
    category: 'beauty' },
  { patterns: ['clothes', 'shoes', 'handbag', 'accessori', 'fashion', 'apparel', 'style',
               'jewellery', 'jewelry', 'watches', 'luxury'],
    category: 'fashion' },
  { patterns: ['fitness', 'gym', 'workout', 'yoga', 'pilates', 'sports', 'athletic', 'activewear',
               'basketball', 'football', 'soccer', 'running', 'cycling', 'boxing', 'mma'],
    category: 'fitness' },
  { patterns: ['gaming', 'gamer', 'esports', 'twitch', 'video game', 'streamer'],
    category: 'gaming' },
  { patterns: ['food', 'cooking', 'recipe', 'restaurant', 'grocery', 'beverage', 'coffee', 'tea',
               'wine', 'beer', 'spirits', 'baking', 'chef'],
    category: 'food' },
  { patterns: ['travel', 'tourism', 'adventure', 'aviation', 'backpack', 'nomad'],
    category: 'travel' },
  { patterns: ['music', 'singer', 'rapper', 'band', 'hip-hop', 'pop', 'concert', 'festival',
               'r&b', 'edm', 'country'],
    category: 'music' },
  { patterns: ['tech', 'technology', 'electronics', 'gadget', 'computer', 'smartphone', 'crypto'],
    category: 'tech' },
  { patterns: ['home', 'decor', 'furniture', 'interior', 'garden', 'candle', 'plants'],
    category: 'lifestyle' },
  { patterns: ['wellness', 'meditation', 'mindfulness', 'holistic', 'vegan', 'organic', 'self-care'],
    category: 'wellness' },
  { patterns: ['family', 'parenting', 'mom', 'dad', 'baby', 'kids', 'children', 'relationships'],
    category: 'lifestyle' },
  { patterns: ['shopping', 'retail', 'lifestyle'],
    category: 'lifestyle' },
  { patterns: ['comedy', 'humor', 'entertainment', 'film', 'television', 'celebrity'],
    category: 'entertainment' },
];

/**
 * Derive primary_category and secondary_categories from the creator's OWN
 * interest labels (the "Creator Interests" section in Modash).
 * Falls back to null if no mapping is found.
 */
export function deriveCategoryFromCreatorInterests(
  creatorInterests: string[],
): { primary: string | null; secondary: string[] } {
  const mapped: string[] = [];
  for (const interest of creatorInterests) {
    const lower = interest.toLowerCase();
    for (const { patterns, category } of CREATOR_INTEREST_TO_CATEGORY) {
      if (patterns.some(p => lower.includes(p)) && !mapped.includes(category)) {
        mapped.push(category);
        break;
      }
    }
    if (mapped.length >= 4) break;
  }
  return { primary: mapped[0] ?? null, secondary: mapped.slice(1) };
}

// ── Section-specific parsers ───────────────────────────────────────────────

export function parseGenderSection(
  sectionText: string | null,
): { male?: number; female?: number } | null {
  if (!sectionText) return null;
  const result: { male?: number; female?: number } = {};

  // ── 1. 3-col format (standard Modash tabular export) ─────────────────
  // e.g. "Female 2,013,198 / 79.37%  247,384,228 / 63.19%"
  //       label   likers_count / likers_pct  followers_count / followers_pct
  // parseThreeColRows correctly extracts label='Female', followers.pct=0.6319
  // We prefer the followers column (index 2); fall back to likers column.
  const kind = detectTableKind(sectionText);
  if (kind === 'three_col') {
    const rows = parseThreeColRows(sectionText);
    for (const row of rows) {
      const l = row.label.toLowerCase().trim();
      const pct = row.followers?.pct ?? row.likers?.pct ?? null;
      if (pct == null) continue;
      if (l === 'female' || l === 'women') result.female = pct;
      else if (l === 'male' || l === 'men') result.male = pct;
    }
  }

  // ── 2. 2-col format (some older formats / simple view) ────────────────
  if (result.male == null && result.female == null) {
    const rows = parseTwoColPercentRows(sectionText);
    for (const { label, pctDecimal } of rows) {
      const l = label.toLowerCase().trim();
      if (l === 'male' || l === 'men') result.male = pctDecimal;
      else if (l === 'female' || l === 'women') result.female = pctDecimal;
    }
  }

  // ── 3. Line-scoped fallback (no cross-line matching to prevent flip bug) ─
  // The old multi-line regex "/([\d.]+)%\s*\bmale/i" could match "63.19%\nMale"
  // which assigns the FEMALE percentage to the male label — fixed here by
  // scanning line-by-line only.
  if (result.male == null && result.female == null) {
    for (const raw of sectionText.split('\n')) {
      const line = normalizeLine(raw);
      const mL   = line.match(/\bmale\b[^%]*([\d.]+)%/i)   ?? line.match(/([\d.]+)%[^%\n]*\bmale\b/i);
      const fL   = line.match(/\bfemale\b[^%]*([\d.]+)%/i) ?? line.match(/([\d.]+)%[^%\n]*\bfemale\b/i);
      if (mL && result.male   == null) result.male   = parsePercentToDecimal(mL[1] + '%') ?? undefined;
      if (fL && result.female == null) result.female = parsePercentToDecimal(fL[1] + '%') ?? undefined;
    }
  }

  if (result.male == null && result.female == null) return null;
  // Infer missing side
  if (result.male != null && result.female == null) result.female = parseFloat((1 - result.male).toFixed(4));
  if (result.female != null && result.male == null) result.male   = parseFloat((1 - result.female).toFixed(4));
  return result;
}

export function parseAgeSection(sectionText: string | null): AudienceMetricRow[] | null {
  if (!sectionText) return null;
  const kind = detectTableKind(sectionText);
  const rows = kind === 'three_col'
    ? threeColToAudienceRows(parseThreeColRows(sectionText))
    : twoColToAudienceRows(parseTwoColPercentRows(sectionText));
  // Filter to plausible age ranges
  const filtered = rows.filter(r => /^\d{1,2}[-–+]/.test(r.label) || /^\d{2}\+$/.test(r.label));
  return filtered.length > 0 ? filtered : null;
}

export function parseGeoSection(sectionText: string | null): AudienceMetricRow[] | null {
  if (!sectionText) return null;
  const kind = detectTableKind(sectionText);
  const rows = kind === 'three_col'
    ? threeColToAudienceRows(parseThreeColRows(sectionText))
    : twoColToAudienceRows(parseTwoColPercentRows(sectionText));
  if (rows.length === 0) return null;
  // Sort by followers_pct desc, take top 15
  return rows
    .filter(r => r.followers_pct != null || r.likers_pct != null)
    .sort((a, b) => (b.followers_pct ?? 0) - (a.followers_pct ?? 0))
    .slice(0, 15);
}

export function parseInterestsSection(sectionText: string | null): AudienceMetricRow[] | null {
  if (!sectionText) return null;
  const kind = detectTableKind(sectionText);
  const rows = kind === 'three_col'
    ? threeColToAudienceRows(parseThreeColRows(sectionText))
    : twoColToAudienceRows(parseTwoColPercentRows(sectionText));
  if (rows.length === 0) return null;
  return rows
    .sort((a, b) => (b.followers_pct ?? 0) - (a.followers_pct ?? 0))
    .slice(0, 15);
}

export function parseBrandSection(sectionText: string | null): AudienceMetricRow[] | null {
  if (!sectionText) return null;
  const kind = detectTableKind(sectionText);
  const rows = kind === 'three_col'
    ? threeColToAudienceRows(parseThreeColRows(sectionText))
    : twoColToAudienceRows(parseTwoColPercentRows(sectionText));
  if (rows.length === 0) return null;
  return rows
    .sort((a, b) => (b.followers_pct ?? 0) - (a.followers_pct ?? 0))
    .slice(0, 15);
}

// ── Derive legacy scalar fields ────────────────────────────────────────────

export function deriveAgeMinMax(
  rows: AudienceMetricRow[] | null,
): { min: number | null; max: number | null } {
  if (!rows || rows.length === 0) return { min: null, max: null };
  const sorted = [...rows].sort((a, b) => {
    const ai = parseInt(a.label);
    const bi = parseInt(b.label);
    return ai - bi;
  });
  let cumPct = 0;
  let min: number | null = null;
  let max: number | null = null;
  for (const row of sorted) {
    const parts = row.label.replace('+', '-99').split(/[-–]/);
    const lo = parseInt(parts[0]);
    const hi = parseInt(parts[1] ?? parts[0]);
    if (min === null) min = lo;
    max = hi;
    cumPct += row.followers_pct ?? 0;
    if (cumPct >= 0.75) break;
  }
  return { min, max };
}

export function deriveGenderAffinity(
  dist: { male?: number; female?: number } | null,
): 'male' | 'female' | 'unisex' {
  if (!dist) return 'unisex';
  const { male = 0, female = 0 } = dist;
  if (female > 0.55) return 'female';
  if (male   > 0.55) return 'male';
  return 'unisex';
}

// ── Content category detection ────────────────────────────────────────────
//
// Scans the CREATOR CONTEXT — sections that describe the creator themselves
// (top of PDF, popular mentions, popular hashtags, creator interests) — NOT
// the full text. Scanning the full text causes misclassification because the
// audience interests section contains every category keyword (e.g. a beauty
// creator's audience has 4.41% gaming interest → "gaming" word appears in the
// PDF and was incorrectly detected as the creator's category).
//
// ORDER: most-specific terms first. beauty/makeup/skincare terms are checked
// before generic "gaming" or "lifestyle" so that beauty creators are never
// misclassified just because their audience partially overlaps gaming.

const KNOWN_CATEGORIES = [
  // ── Beauty / cosmetics (most distinctive — check first) ──────────────
  'makeup', 'skincare', 'haircare', 'cosmetics', 'lashes', 'nail',
  // ── Specific sports ──────────────────────────────────────────────────
  'basketball', 'nba', 'football', 'nfl', 'soccer', 'baseball', 'tennis',
  'golf', 'boxing', 'mma', 'wrestling', 'swimming', 'running', 'cycling',
  // ── Fashion / apparel ────────────────────────────────────────────────
  'streetwear', 'fashion',
  // ── Mid-level categories ─────────────────────────────────────────────
  'fitness', 'sports', 'beauty', 'music', 'comedy', 'gaming',
  'health', 'food', 'travel', 'wellness', 'education',
  // ── Generic — check last; appear in almost all Modash PDFs ───────────
  'entertainment', 'lifestyle',
];

/**
 * Extract the "creator context" text — the portion of the PDF that describes
 * the creator's OWN content, brand and interests — by slicing before the first
 * audience-data table heading.  Also appends the Popular hashtags and Popular
 * mentions sections since they reflect the creator's own posts.
 *
 * This prevents audience interest keywords (e.g. "Gaming 4.41%") from
 * contaminating the creator category scan.
 */
export function extractCreatorContextText(
  fullText: string,
  extractedSections: Record<string, string>,
): string {
  // Stop at the first heading that introduces AUDIENCE data tables
  const AUDIENCE_BOUNDARY_HEADINGS = [
    'Gender split', 'Age split', 'Location by Country', 'Location by City',
    'Audience interests', 'Audience brand affinity',
  ];
  let boundary = fullText.length;
  for (const h of AUDIENCE_BOUNDARY_HEADINGS) {
    const re = new RegExp('(?:^|\\n)[ \\t]*' + escapeRegex(h) + '[ \\t]*(?:\\n|$)', 'i');
    const m  = re.exec(fullText);
    if (m && m.index < boundary) boundary = m.index;
  }
  // Limit to first 4000 chars before the boundary (handles very long PDFs)
  const creatorHead = fullText.slice(0, Math.min(boundary, 4000));

  // Always include mentions and hashtags — they are creator-generated signals
  const mentions  = extractedSections['Popular mentions']  ?? '';
  const hashtags  = extractedSections['Popular hashtags']  ?? '';

  return [creatorHead, mentions, hashtags].filter(Boolean).join('\n');
}

export function extractCategories(text: string): { primary: string | null; secondary: string[] } {
  let primary: string | null = null;
  const secondary: string[] = [];
  for (const cat of KNOWN_CATEGORIES) {
    if (new RegExp('\\b' + cat + '\\b', 'i').test(text)) {
      const norm = cat.toLowerCase();
      if (!primary) primary = norm;
      else if (!secondary.includes(norm)) secondary.push(norm);
      if (secondary.length >= 3) break;
    }
  }
  return { primary, secondary };
}

// ── Main text-based parser (called by parsePdf) ────────────────────────────

export function parseModashText(fullText: string): ParsedModashReport {
  const warnings: string[] = [];
  const extractedSections: Record<string, string> = {};
  const detectedTables: ParsedModashReport['debug']['detectedTables'] = [];

  // ── Extract all sections ───────────────────────────────────
  for (const h of ALL_HEADINGS) {
    const s = extractSectionByHeadings(fullText, h);
    if (s) extractedSections[h] = s;
  }

  // ── Core metrics ───────────────────────────────────────────
  const followers      = extractFollowers(fullText);
  const engagement_rate = extractEngagementRate(fullText);
  const avg_likes      = extractAvgLikes(fullText);
  const avg_views      = extractAvgViews(fullText);

  if (!followers)       warnings.push('Could not parse follower count');
  if (!engagement_rate) warnings.push('Could not parse engagement rate');
  if (!avg_likes)       warnings.push('Could not parse avg likes');

  // ── Platform / handle ─────────────────────────────────────
  let platform: ParsedModashReport['platform'] = null;
  let handle: string | null = null;
  let profile_url: string | null = null;
  let location_text: string | null = null;

  // 1) Try to find a social media URL, strip query string / trailing slash
  //    e.g. https://www.instagram.com/dominiquedaniell3/?hl=en → dominiquedaniell3
  //    PDF.js may insert spaces around slashes/@ symbols, so allow optional \s*
  const urlM = fullText.match(
    /https?\s*:\s*\/\s*\/\s*(?:www\s*\.\s*)?(instagram\s*\.\s*com|tiktok\s*\.\s*com|youtube\s*\.\s*com)\s*\/\s*@?\s*([A-Za-z0-9_.]+)/i
  );
  if (urlM) {
    const domain = urlM[1].replace(/\s+/g, '').toLowerCase();
    platform = domain.includes('instagram') ? 'instagram'
      : domain.includes('tiktok') ? 'tiktok'
      : domain.includes('youtube') ? 'youtube'
      : 'other';
    handle = urlM[2];
    profile_url = `https://www.${domain}/${handle}`;
  }

  // 2) Fallback / verification: look for @handle in the first ~1200 chars.
  //    Modash PDFs always show the handle as "@username" near the top.
  //    PDF.js may place "@" and the handle on the same line (with spaces) or
  //    on adjacent lines if the glyph Y-coords differ. We try three patterns
  //    in order of reliability:
  //      a) @handle on the same line (space-tolerant)
  //      b) @\n followed by the handle as the first word on the next line
  //      c) a line containing only "@" followed by a short next line (handle)
  const topText = fullText.slice(0, 1200);

  // a) Same-line: "@handle" or "@ handle"
  let atCandidate: string | null = null;
  const atSameLine = topText.match(/@[ \t]*([A-Za-z0-9_.]{2,40})/);
  if (atSameLine) {
    atCandidate = atSameLine[1];
  }

  // b) Cross-line: "@" at end of a line, handle at start of next line
  if (!atCandidate) {
    const atCrossLine = topText.match(/@[ \t]*\n[ \t]*([A-Za-z0-9_.]{2,40})/);
    if (atCrossLine) atCandidate = atCrossLine[1];
  }

  if (atCandidate) {
    if (!handle) {
      handle = atCandidate;
    } else if (atCandidate.toLowerCase() !== handle.toLowerCase()) {
      handle = atCandidate;
    }
  }

  // ── Gender split ───────────────────────────────────────────
  const gender_distribution = parseGenderSection(extractedSections['Gender split'] ?? null);
  if (!gender_distribution) warnings.push('Could not parse gender split');

  // ── Age splits ─────────────────────────────────────────────
  const age_distribution_overall = parseAgeSection(extractedSections['Age split'] ?? null);
  const age_distribution_male    = parseAgeSection(extractedSections['Male age split'] ?? null);
  const age_distribution_female  = parseAgeSection(extractedSections['Female age split'] ?? null);
  if (!age_distribution_overall) warnings.push('Could not parse age distribution');

  // ── Geo distribution ───────────────────────────────────────
  const geo_distribution = parseGeoSection(extractedSections['Location by Country'] ?? null);
  if (!geo_distribution) warnings.push('Could not parse country distribution');

  // ── Interests (prefer 3-col Audience interests, fallback 2-col Follower Interests) ──
  let interests_ranked = parseInterestsSection(extractedSections['Audience interests'] ?? null);
  if (!interests_ranked) {
    interests_ranked = parseInterestsSection(extractedSections['Follower Interests'] ?? null);
  }
  if (!interests_ranked) warnings.push('Could not parse audience interests');

  // ── Brand affinity (prefer Audience brand affinity, fallback Follower brand affinity) ──
  let brand_affinity_ranked = parseBrandSection(extractedSections['Audience brand affinity'] ?? null);
  if (!brand_affinity_ranked) {
    brand_affinity_ranked = parseBrandSection(extractedSections['Follower brand affinity'] ?? null);
  }

  // ── Hashtags and mentions (2-col) ─────────────────────────
  const hashSection = extractedSections['Popular hashtags'] ?? null;
  const mentionSection = extractedSections['Popular mentions'] ?? null;

  const popular_hashtags = hashSection
    ? parseTwoColPercentRows(hashSection).map(r => ({ tag: r.label, pct: r.pctDecimal }))
    : null;
  const popular_mentions = mentionSection
    ? parseTwoColPercentRows(mentionSection).map(r => ({ mention: r.label, pct: r.pctDecimal }))
    : null;

  // ── Also try multi-2col block for missing tables ───────────
  if (!interests_ranked || !brand_affinity_ranked) {
    // Try to find and parse multi-2col block containing follower brand/interests
    const multiBlock = (extractedSections['Follower brand affinity'] ?? '')
      + '\n' + (extractedSections['Follower Interests'] ?? '');
    if (multiBlock.trim()) {
      const multiTables = parseMultiTwoColTables(multiBlock);
      for (const t of multiTables) {
        const title = t.title.toLowerCase();
        if (!interests_ranked && (title.includes('interest'))) {
          interests_ranked = twoColToAudienceRows(t.rows);
        }
        if (!brand_affinity_ranked && (title.includes('brand'))) {
          brand_affinity_ranked = twoColToAudienceRows(t.rows);
        }
      }
    }
  }

  // ── Build detectedTables debug info ───────────────────────
  for (const [heading, sectionText] of Object.entries(extractedSections)) {
    const kind = detectTableKind(sectionText);
    const rows = kind === 'three_col'
      ? parseThreeColRows(sectionText)
      : parseTwoColPercentRows(sectionText);
    detectedTables.push({ title: heading, kind, rowCount: rows.length });
  }

  // ── Creator's OWN interests and brand affiliations ────────────────────
  // These appear in the Modash PDF as plain label lists (no percentages).
  // They are the STRONGEST signal for what products this creator would sell.
  const creator_interests     = parseCreatorLabelList(extractedSections['Creator Interests']     ?? null);
  const creator_brand_affinity = parseCreatorLabelList(extractedSections['Creator brand affinity'] ?? null);

  if (!creator_interests)      warnings.push('Could not parse Creator Interests (section not found or empty)');
  if (!creator_brand_affinity) warnings.push('Could not parse Creator brand affinity (section not found or empty)');

  // ── Categories: use creator's OWN interests first, fall back to text scan ──
  // Scanning audience interest text causes misclassification (e.g. a beauty
  // creator whose audience has 4% gaming interest would be labelled "gaming").
  // The creator's declared interests are always more reliable.
  let primary_category:     string | null = null;
  let secondary_categories: string[]      = [];

  if (creator_interests && creator_interests.length > 0) {
    const catResult = deriveCategoryFromCreatorInterests(creator_interests);
    primary_category     = catResult.primary;
    secondary_categories = catResult.secondary;
  }

  if (!primary_category) {
    // Fallback: scan the creator-context portion of the PDF
    // (everything before audience sections + mentions + hashtags).
    const creatorContext = extractCreatorContextText(fullText, extractedSections);
    const catResult      = extractCategories(creatorContext);
    primary_category     = catResult.primary;
    secondary_categories = catResult.secondary;
  }

  return {
    platform,
    handle,
    profile_url,
    location_text,
    followers,
    engagement_rate,
    avg_likes,
    avg_views,
    creator_interests,
    creator_brand_affinity,
    gender_distribution,
    age_distribution_overall,
    age_distribution_male,
    age_distribution_female,
    geo_distribution,
    interests_ranked,
    brand_affinity_ranked,
    popular_hashtags,
    popular_mentions,
    primary_category,
    secondary_categories,
    parseWarnings: warnings,
    debug: { extractedSections, detectedTables },
  };
}
